import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AttendanceRequestStatus,
  type DeviceCreate,
  type DeviceItem,
  type DeviceListResponse,
  type DeviceMapCreate,
  type DeviceMapListResponse,
  type DeviceMapResult,
  type DeviceUpdate,
  type DeviceWithSecret,
  type AttendanceDay,
  type AttendanceGrid,
  type AttendanceTodaySummary,
  type FlaggedPunchListResponse,
  type MyAttendanceMonth,
  type TeamToday,
  type PunchAdd,
  type RegularisationCreate,
  type RegularisationItem,
  type RegularisationListResponse,
  type RegularisationScope,
  type UnmappedListResponse,
  type AttendanceToday,
  type OfficeCreate,
  type OfficeItem,
  type OfficeListResponse,
  type OfficeUpdate,
  type PunchCreate,
  type PunchItem,
  type WfhCalendarResponse,
  type WfhListResponse,
  type WfhRequestCreate,
  type WfhRequestItem,
  type WfhScope,
  attendanceTodaySchema,
  deviceItemSchema,
  deviceListResponseSchema,
  deviceMapListResponseSchema,
  deviceMapResultSchema,
  deviceWithSecretSchema,
  attendanceDaySchema,
  attendanceGridSchema,
  attendanceTodaySummarySchema,
  flaggedPunchListResponseSchema,
  myAttendanceMonthSchema,
  teamTodaySchema,
  regularisationItemSchema,
  regularisationListResponseSchema,
  officeItemSchema,
  officeListResponseSchema,
  punchItemSchema,
  unmappedListResponseSchema,
  wfhCalendarResponseSchema,
  wfhListResponseSchema,
  wfhRequestItemSchema,
} from '@thrivehrm/shared';
import { API_BASE, api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';

export interface WfhListParams {
  scope: WfhScope;
  page: number;
  pageSize: number;
  status?: readonly AttendanceRequestStatus[];
  from?: string;
  to?: string;
}

/** `GET /wfh` by scope (me / team / all), newest first; `enabled: false` skips the fetch. */
export function useWfhRequests(params: WfhListParams, { enabled = true } = {}) {
  return useQuery({
    enabled,
    queryKey: queryKeys.attendance.wfh(params),
    queryFn: ({ signal }) =>
      api.get<WfhListResponse>('/wfh', {
        query: {
          scope: params.scope,
          page: String(params.page),
          pageSize: String(params.pageSize),
          ...(params.status?.length ? { status: params.status } : {}),
          ...(params.from ? { from: params.from } : {}),
          ...(params.to ? { to: params.to } : {}),
        },
        schema: wfhListResponseSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

/** `GET /wfh/calendar` — pending and approved WFH days in a range (≤ 62 days). */
export function useWfhCalendar(params: { scope: 'team' | 'all'; from: string; to: string }) {
  return useQuery({
    queryKey: queryKeys.attendance.wfhCalendar(params),
    queryFn: ({ signal }) =>
      api.get<WfhCalendarResponse>('/wfh/calendar', {
        query: params,
        schema: wfhCalendarResponseSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

/** WFH writes (FR-WFH-1/3). Errors are `ApiError`s (422 with the field and a code). */
export const wfhApi = {
  create: (body: WfhRequestCreate) =>
    api.post<WfhRequestItem>('/wfh/requests', body, { schema: wfhRequestItemSchema }),
  cancel: (id: string) =>
    api.post<WfhRequestItem>(`/wfh/requests/${id}/cancel`, {}, { schema: wfhRequestItemSchema }),
};

/** A WFH write that refreshes attendance views and the approvals inbox once it succeeds. */
export function useWfhMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.attendance.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.approvals.all }),
      ]),
  });
}

// ── Office locations (FR-ATT-2, attendance.manage) ───────────────────────────────────────────

/** `GET /attendance/offices` — every office and the radius new ones get. */
export function useOffices() {
  return useQuery({
    queryKey: queryKeys.attendance.offices,
    queryFn: ({ signal }) =>
      api.get<OfficeListResponse>('/attendance/offices', {
        schema: officeListResponseSchema,
        signal,
      }),
  });
}

/** Office writes. Errors are `ApiError`s (400 with the field, e.g. `ipRanges.1`). */
export const officesApi = {
  create: (body: OfficeCreate) =>
    api.post<OfficeItem>('/attendance/offices', body, { schema: officeItemSchema }),
  update: ({ id, patch }: { id: string; patch: OfficeUpdate }) =>
    api.patch<OfficeItem>(`/attendance/offices/${id}`, patch, { schema: officeItemSchema }),
};

// ── App check-in / out (FR-ATT-3, attendance.checkin) ─────────────────────────────────────────

/** `GET /attendance/today` — my punches today and what the today card offers next. */
export function useAttendanceToday() {
  return useQuery({
    queryKey: queryKeys.attendance.today,
    queryFn: ({ signal }) =>
      api.get<AttendanceToday>('/attendance/today', { schema: attendanceTodaySchema, signal }),
  });
}

/** `POST /attendance/punch`; refreshes the today card (and other attendance views) after. */
export function usePunch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PunchCreate) =>
      api.post<PunchItem>('/attendance/punch', body, { schema: punchItemSchema }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.attendance.all }),
  });
}

// ── Biometric devices (FR-ATT-5/6, attendance.manage) ────────────────────────────────────────

/** `GET /attendance/devices` — registered devices, last contact and queued punches. */
export function useDevices() {
  return useQuery({
    queryKey: queryKeys.attendance.devices,
    queryFn: ({ signal }) =>
      api.get<DeviceListResponse>('/attendance/devices', {
        schema: deviceListResponseSchema,
        signal,
      }),
  });
}

/** `GET /attendance/devices/unmapped` — device users whose punches matched no employee. */
export function useUnmappedPunches() {
  return useQuery({
    queryKey: queryKeys.attendance.unmapped,
    queryFn: ({ signal }) =>
      api.get<UnmappedListResponse>('/attendance/devices/unmapped', {
        schema: unmappedListResponseSchema,
        signal,
      }),
  });
}

/** `GET /attendance/devices/:id/map` — the device's user-id overrides. */
export function useDeviceMaps(deviceId: string) {
  return useQuery({
    queryKey: queryKeys.attendance.deviceMaps(deviceId),
    queryFn: ({ signal }) =>
      api.get<DeviceMapListResponse>(`/attendance/devices/${deviceId}/map`, {
        schema: deviceMapListResponseSchema,
        signal,
      }),
  });
}

/** Device writes. Errors are `ApiError`s (400 / 422 with the field, 409 duplicate serial). */
export const devicesApi = {
  create: (body: DeviceCreate) =>
    api.post<DeviceWithSecret>('/attendance/devices', body, { schema: deviceWithSecretSchema }),
  update: ({ id, patch }: { id: string; patch: DeviceUpdate }) =>
    api.patch<DeviceItem>(`/attendance/devices/${id}`, patch, { schema: deviceItemSchema }),
  rotateSecret: (id: string) =>
    api.post<DeviceWithSecret>(
      `/attendance/devices/${id}/secret`,
      {},
      {
        schema: deviceWithSecretSchema,
      },
    ),
  map: ({ deviceId, body }: { deviceId: string; body: DeviceMapCreate }) =>
    api.post<DeviceMapResult>(`/attendance/devices/${deviceId}/map`, body, {
      schema: deviceMapResultSchema,
    }),
  unmap: ({ deviceId, mapId }: { deviceId: string; mapId: string }) =>
    api.delete<undefined>(`/attendance/devices/${deviceId}/map/${mapId}`),
};

/** A device write that refreshes devices, overrides and the unmapped queue once it succeeds. */
export function useDeviceMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.attendance.devices }),
  });
}

// ── Regularisation & HR corrections (FR-ATT-9…11) ────────────────────────────────────────────

/** `GET /attendance/regularisations?scope=me|all`, newest first. */
export function useRegularisations(
  params: { scope: RegularisationScope; page: number; pageSize: number },
  { enabled = true } = {},
) {
  return useQuery({
    enabled,
    queryKey: queryKeys.attendance.regularisations(params),
    queryFn: ({ signal }) =>
      api.get<RegularisationListResponse>('/attendance/regularisations', {
        query: {
          scope: params.scope,
          page: String(params.page),
          pageSize: String(params.pageSize),
        },
        schema: regularisationListResponseSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

/** `GET /attendance/punches/flagged` — HR's review queue, oldest first. */
export function useFlaggedPunches(params: { page: number; pageSize: number }) {
  return useQuery({
    queryKey: queryKeys.attendance.flagged(params),
    queryFn: ({ signal }) =>
      api.get<FlaggedPunchListResponse>('/attendance/punches/flagged', {
        query: { page: String(params.page), pageSize: String(params.pageSize) },
        schema: flaggedPunchListResponseSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

/** Corrections. Errors are `ApiError`s (422 with the field and a code, 409 when closed). */
export const correctionsApi = {
  request: (body: RegularisationCreate) =>
    api.post<RegularisationItem>('/attendance/regularisations', body, {
      schema: regularisationItemSchema,
    }),
  withdraw: (id: string) =>
    api.post<RegularisationItem>(
      `/attendance/regularisations/${id}/cancel`,
      {},
      {
        schema: regularisationItemSchema,
      },
    ),
  addPunch: (body: PunchAdd) =>
    api.post<PunchItem>('/attendance/punches', body, { schema: punchItemSchema }),
  voidPunch: ({ id, reason }: { id: string; reason: string }) =>
    api.post<PunchItem>(`/attendance/punches/${id}/void`, { reason }, { schema: punchItemSchema }),
  acceptPunch: (id: string) =>
    api.post<PunchItem>(`/attendance/punches/${id}/review`, {}, { schema: punchItemSchema }),
};

/** A correction that refreshes attendance views and the approvals inbox once it succeeds. */
export const useCorrectionMutation = useWfhMutation;

// ── Views (FR-ATT-11/12) ─────────────────────────────────────────────────────────────────────

/** `GET /me/attendance?month=` — my days, punches and totals. */
export function useMyAttendanceMonth(month: string, { enabled = true } = {}) {
  return useQuery({
    enabled,
    queryKey: queryKeys.attendance.myMonth(month),
    queryFn: ({ signal }) =>
      api.get<MyAttendanceMonth>('/me/attendance', {
        query: { month },
        schema: myAttendanceMonthSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

/** `GET /attendance/team/today` — where my team is today (refreshes every minute). */
export function useTeamToday() {
  return useQuery({
    queryKey: queryKeys.attendance.teamToday,
    queryFn: ({ signal }) =>
      api.get<TeamToday>('/attendance/team/today', { schema: teamTodaySchema, signal }),
    refetchInterval: 60_000,
  });
}

/** Month grid: `team` (team.read) or `all` — the register (attendance.read.all). */
export function useAttendanceGrid(params: {
  scope: 'team' | 'all';
  month: string;
  q: string;
  page: number;
  pageSize: number;
}) {
  return useQuery({
    queryKey: queryKeys.attendance.grid(params),
    queryFn: ({ signal }) =>
      api.get<AttendanceGrid>(
        params.scope === 'team' ? '/attendance/team' : '/attendance/records',
        {
          query: {
            month: params.month,
            page: String(params.page),
            pageSize: String(params.pageSize),
            ...(params.q ? { q: params.q } : {}),
          },
          schema: attendanceGridSchema,
          signal,
        },
      ),
    placeholderData: keepPreviousData,
  });
}

/** `GET /attendance/day` — one person's day (location only when the API allows it). */
export function useAttendanceDay(params: { employeeId: string; date: string } | null) {
  return useQuery({
    enabled: params !== null,
    queryKey: queryKeys.attendance.day(params ?? {}),
    queryFn: ({ signal }) =>
      api.get<AttendanceDay>('/attendance/day', {
        query: params ?? {},
        schema: attendanceDaySchema,
        signal,
      }),
  });
}

/** The monthly summary workbook (attendance.read.all). */
export const attendanceSummaryExportUrl = (month: string) =>
  `${API_BASE}/exports/attendance-summary.xlsx?month=${month}`;

/** `GET /attendance/summary/today` — company counts for the HR dashboard (every minute). */
export function useAttendanceTodaySummary() {
  return useQuery({
    queryKey: queryKeys.attendance.todaySummary,
    queryFn: ({ signal }) =>
      api.get<AttendanceTodaySummary>('/attendance/summary/today', {
        schema: attendanceTodaySummarySchema,
        signal,
      }),
    refetchInterval: 60_000,
  });
}
